import { Bell, Bookmark, CalendarDays, MessageCircle, Phone, Users } from "@fg/icons";
import { HubConnectionState } from "@microsoft/signalr";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n";

export type AppMode = "teams" | "dms" | "groups" | "calendar" | "history" | "saved" | "activity";

/**
 * The suite rail (approved suite-shell mock-up): 48px, icon buttons with the
 * name as tooltip / accessible label, an accent-tinted fill plus a 3px accent
 * bar on the current app, accent unread counts, and a small live dot for the
 * realtime connection at the foot.
 */
export function AppRail({ mode, onChange, unreadChats, activityCount, hubState }: {
    mode: AppMode;
    onChange: (mode: AppMode) => void;
    unreadChats: number;
    activityCount: number;
    hubState: HubConnectionState;
}) {
    const t = useT();
    const items: Array<{ id: AppMode; label: string; Icon: typeof Bell; count?: number; active?: boolean }> = [
        { id: "activity", label: "Activity", Icon: Bell, count: activityCount },
        { id: "dms", label: t("mode.chats"), Icon: MessageCircle, count: unreadChats, active: mode === "dms" || mode === "groups" },
        { id: "teams", label: t("mode.channels"), Icon: Users },
        { id: "calendar", label: t("mode.calendar"), Icon: CalendarDays },
        { id: "history", label: "Calls", Icon: Phone },
        { id: "saved", label: t("mode.saved"), Icon: Bookmark }
    ];
    const live = hubState === HubConnectionState.Connected;
    const liveLabel = live ? "Connected — updates are live"
        : hubState === HubConnectionState.Reconnecting ? "Reconnecting…"
        : hubState === HubConnectionState.Connecting ? "Connecting…"
        : "Offline — retrying";

    return (
        <nav
            aria-label="Connect apps"
            className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-[var(--connect-line)] bg-[var(--connect-pane)] pt-2"
        >
            {items.map((it) => {
                const active = it.active ?? mode === it.id;
                const count = it.count ?? 0;
                return (
                    <button
                        key={it.id}
                        type="button"
                        onClick={() => onChange(it.id)}
                        aria-current={active ? "page" : undefined}
                        aria-label={count > 0 ? `${it.label}, ${count} unread` : it.label}
                        title={it.label}
                        className={cn(
                            "relative flex h-10 w-10 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            active
                                ? "bg-[var(--connect-primary-soft)] text-primary"
                                : "text-[var(--connect-text-2)] hover:bg-[var(--connect-hover)] hover:text-foreground"
                        )}
                    >
                        {active ? <span aria-hidden className="absolute -left-1 bottom-2.5 top-2.5 w-[3px] rounded-sm bg-primary" /> : null}
                        <it.Icon className="h-5 w-5" strokeWidth={1.75} />
                        {count > 0 ? (
                            <span className="absolute right-[3px] top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 tabular-nums text-primary-foreground">
                                {count > 99 ? "99+" : count}
                            </span>
                        ) : null}
                    </button>
                );
            })}
            <div className="mt-auto flex flex-col items-center pb-3" title={liveLabel} aria-label={liveLabel} role="status">
                <span className="relative flex h-2.5 w-2.5">
                    {live ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /> : null}
                    <span className={cn(
                        "relative inline-flex h-2.5 w-2.5 rounded-full",
                        live ? "bg-emerald-500" : hubState === HubConnectionState.Disconnected ? "bg-rose-500" : "bg-amber-400"
                    )} />
                </span>
            </div>
        </nav>
    );
}
